"use client";
import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import Image from "next/image";
import { useTranslation } from "react-i18next";

// ─── Assets ─────────────────────────────────────────────────────────────────
const BG_IMG = "https://getgeek-website-assets.b-cdn.net/LeapSoftLaunchBGImg.webp";
const RIGHT_IMG = "https://getgeek-website-assets.b-cdn.net/LeapSoftLaunchrightsideImg.webp";

// ─── Styled Components ──────────────────────────────────────────────────────
const LeapSection = styled(Box)(({ theme }) => ({
  position: "relative",
  backgroundImage: `url(${BG_IMG})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundColor: "#5B21B6",
  padding: "72px 0",
    scrollMarginTop: "90px",

  [theme.breakpoints.down("sm")]: {
    padding: "48px 0",
  },
}));

const TopRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "40px",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    gap: "32px",
  },
}));

const LeftContent = styled(Box)(({ theme }) => ({
  flex: "0 0 50%",
  maxWidth: "50%",
  [theme.breakpoints.down("md")]: {
    flex: "0 0 100%",
    maxWidth: "100%",
  },
}));

const RightImageWrapper = styled(Box)(({ theme }) => ({
  flex: "0 0 42%",
  maxWidth: "580px",
  marginInlineStart: "auto",
  lineHeight: 0,
  [theme.breakpoints.down("md")]: {
    flex: "0 0 100%",
    maxWidth: "100%",
    marginInlineStart: 0,
  },
}));

const Eyebrow = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ theme, isRTL }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "15px",
  fontWeight: 700,
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: "#E9D5FF",
  marginBottom: "18px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
  },
  "&::before": {
    content: '""',
    display: "inline-block",
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#E9D5FF",
  },
}));

const Heading = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ theme, isRTL }) => ({
  fontFamily: isRTL ? "var(--font-plusjakarta)" : "var(--font-plusjakarta)",
  fontWeight: 700,
  fontSize: "35px",
  lineHeight: 1.3,
  color: "#FFFFFF",
  marginBottom: "16px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "25px",
  },
}));

const Subtext = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "16px",
  lineHeight: 1.8,
  color: "rgba(255,255,255,0.8)",
  maxWidth: "800px",
  marginBottom: "28px",
}));

const PillsRow = styled(Box)(() => ({
  display: "flex",
  flexWrap: "wrap",
  gap: "10px",
  marginBottom: "24px",
}));

const Pill = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "13px",
  fontWeight: 500,
  color: "#FFFFFF",
  padding: "8px 18px",
  borderRadius: "999px",
  border: "1px solid rgba(255,255,255,0.3)",
  backgroundColor: "rgba(255,255,255,0.06)",
}));

const FooterText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "15px",
  lineHeight: 1.6,
  color: "#fff",
}));

// ─── Component ───────────────────────────────────────────────────────────────
const LeapSoftLaunch = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const pills = t("leapSoftLaunchSection.pills", { returnObjects: true });

  return (
    <LeapSection id="getgeek-section">
      <Container maxWidth="xl">
        <TopRow>
          <LeftContent>
            <Eyebrow isRTL={isRTL}>
              {t("leapSoftLaunchSection.eyebrow")}
            </Eyebrow>

            <Heading variant="h2" isRTL={isRTL}>
              {t("leapSoftLaunchSection.heading")}
            </Heading>

            <Subtext isRTL={isRTL}>
              {t("leapSoftLaunchSection.subtext")}
            </Subtext>

            <PillsRow>
              {pills.map((pill, index) => (
                <Pill key={index} isRTL={isRTL}>
                  {pill}
                </Pill>
              ))}
            </PillsRow>

            <FooterText isRTL={isRTL}>
              {t("leapSoftLaunchSection.footerText")}
            </FooterText>
          </LeftContent>

          <RightImageWrapper>
            <Image
              src={RIGHT_IMG}
              alt="Getgeek at LEAP 2026"
              width={480}
              height={380}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                objectFit: "contain",
                borderRadius: "16px",
              }}
            />
          </RightImageWrapper>
        </TopRow>
      </Container>
    </LeapSection>
  );
};

export default LeapSoftLaunch;